<template>
  <div class="panel">
    <div
      v-for="category in categoriesAvailable"
      :key="category.name"
      class="section category"
      :class="{
        selected: selectedCategoryId === category.id,
        openable: openableCategories.includes(category.id),
      }"
    >
      <div class="title" @click.prevent="() => openPanel(category.id)">
        <span class="text">{{ getSectionTitle(category.id) }}</span>
        <div
          v-if="category.canAddItem && category.id !== 'skills'"
          class="add-button"
          :class="{ grey: itemsInCategory(category.id).length % 2 === 0 }"
        >
          <svg
            :class="{ selected: selectedCategoryId === category.id }"
            class="icon"
            viewBox="0 0 100 100"
            xmlns="http://www.w3.org/2000/svg"
            @click.stop="addItem(category.id)"
          >
            <path
              id="Path"
              d="M73.1756415,46.1776988 L53.8240669,46.1776988 L53.8240669,26.8246017 C53.8240669,24.7123951 52.1117807,23 49.9997084,23 C47.8876361,23 46.1753499,24.7126867 46.1753499,26.8246017 L46.1753499,46.1788653 L26.8243585,46.1788653 C24.7125778,46.1788653 23,47.891552 23,50.003467 C23,52.1156737 24.7125778,53.8280688 26.8243585,53.8280688 L46.1744751,53.8280688 L46.1744751,73.1805826 C46.1744751,75.2924977 47.8867613,77.0048927 49.9988336,77.0048927 C52.1106143,77.0048927 53.8231921,75.2924977 53.8231921,73.1805826 L53.8231921,53.8271939 L73.1759331,53.8271939 C75.2874222,53.8271939 77,52.1145072 77,50.0025922 C77,47.8903855 75.2874222,46.1776988 73.1756415,46.1776988 Z"
              fill="currentColor"
            ></path>
          </svg>
        </div>
        <div v-if="!category.canAddItem || category.id === 'skills'" class="spacer" />
        <div v-if="!category.canAddItem || category.id === 'skills'" class="page-number">
          p.{{ category.id === 'contact' ? pages.length + 2 : 1 }}
        </div>
      </div>
      <div v-if="openableCategories.includes(category.id)" :ref="category.id" class="items">
        <div
          v-for="item in itemsInCategory(category.id)"
          :key="item.id"
          class="item"
          :data-id="item.id"
          :class="{
            selected: selectedItemId === item.id && selectedCategoryId === category.id,
            empty: !item.title,
          }"
          @click="itemSelected(item.id)"
        >
          <DragIcon class="icon handle" />
          <span v-if="item.title" class="item-title" :class="item.indentation"
            >{{ item.client ? item.client + ' - ' : '' }}{{ item.title }}</span
          >
          <span v-else class="item-title" :class="item.indentation">Sans titre</span>
          <NextPageIcon v-if="item.content.startNewPage" class="new-page" />
          <div class="page-number">p.{{ pageNumber(itemIndex(item.id)) }}</div>
        </div>
        <div v-if="itemsInCategory(category.id).length === 0" class="item add" @click="addItem(category.id)">
          {{ $t(`editor.panel.addCategory.${category.id}`) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DragIcon from '@/assets/icons/dragIcon.svg';
import NextPageIcon from '@/assets/icons/newPage.svg';
import { Sortable } from '@shopify/draggable';
import { mapGetters } from 'vuex';

const openableCategories = ['formations', 'projects', 'experiences'];
export default {
  name: 'LeftPanelEditor',

  components: { DragIcon, NextPageIcon },

  props: {
    categories: {
      type: Array,
      required: true,
    },
    items: {
      type: Array,
      default: () => [],
    },
    selectCategory: {
      type: Function,
      required: true,
    },
    scrollTo: {
      type: Function,
      required: true,
    },
    selectedItemId: {
      type: String,
      default: '',
    },
    selectedCategoryId: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    resume: {
      type: Object,
      required: true,
    },
    pages: {
      type: Array,
      default: () => [],
    },
  },

  emits: ['itemSelected', 'addItem'],

  data() {
    return {
      user: {},
      sortedItems: [],
      openedMenu: [],
      inputSelected: '',
      openableCategories,
    };
  },

  computed: {
    ...mapGetters(['loggedIn']),
    categoriesAvailable() {
      return this.loggedIn ? this.categories : this.categories.filter(c => !c.loginRequired);
    },
    titles() {
      const titles = this.items.find(i => i.type === 'titles');
      return titles ? titles.content : undefined;
    },
  },

  watch: {
    'resume.items': {
      handler() {
        this.updateItems();
      },
      deep: true,
    },
  },

  mounted() {
    this.updateItems();
    setTimeout(() => {
      this.categories.forEach(category => {
        const sortable = new Sortable(this.$refs[category.id], {
          draggable: '.item',
          handle: '.handle',
          delay: 0,
        });
        sortable.on('sortable:stop', e => {
          if (e.newIndex === e.oldIndex) return;
          const itemId = e.dragEvent.source.dataset.id;
          const items = this.itemsInCategory(category.id).filter(i => i.id !== itemId);
          items.splice(
            e.newIndex,
            0,
            this.itemsInCategory(category.id).find(i => i.id === itemId)
          );
          items.forEach((i, index) => {
            i.order = index;
            this.$store.dispatch('items.edit', { id: i.id, order: index });
          });
        });
      });
    }, 1000);
  },
  methods: {
    getSectionTitle(categorieId) {
      if (this.titles && this.titles[categorieId]) {
        return this.titles[categorieId];
      }

      return this.$t(`resume.sections.${this.resume.language || 'fr'}.${categorieId}`);
    },
    pageNumber(itemIndex) {
      let items = 0;
      for (let i = 0; i < this.pages.length; i++) {
        if (items >= itemIndex) {
          return i + 1;
        }
        items += this.pages[i].nbItem;
      }
      return this.pages.length + 1;
    },
    openPanel(categoryId) {
      this.selectCategory(categoryId);
      this.itemSelected();
    },
    itemSelected(id) {
      this.inputSelected = '';
      this.$emit('itemSelected', id);
    },
    addItem(id) {
      this.$emit('addItem', id);
    },
    itemIndex(itemId) {
      return this.sortedItems.findIndex(i => i.id === itemId);
    },
    itemsInCategory(categoryId) {
      return this.sortedItems.filter(i => i.type === categoryId);
    },
    updateItems() {
      const excludeType = ['titles', 'titles', 'skills', 'conviction'];
      this.sortedItems = this.resume.items
        .filter(i => !excludeType.includes(i.type))
        .map(i => ({
          id: i.id,
          title: i.content.title,
          indentation: i.content && i.content.indentation ? i.content.indentation : 'none',
          client: i.client,
          type: i.type,
          order: i.order,
          content: i.content,
        }));
      this.sortedItems.sort((a, b) => {
        let order = openableCategories.indexOf(a.type) * 100 - openableCategories.indexOf(b.type) * 100;
        order += a.order - b.order;
        return order;
      });
    },
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';

$title-height: 30px;
$item-height: 26px;

.panel {
  border-right: solid 2px colors.$grey-background-color;
}

.section {
  display: flex;
  flex-direction: column;
  .title {
    position: relative;
    height: $title-height;
    line-height: $title-height;
    text-transform: uppercase;
    background-color: white;
    font-size: 11px;
    font-weight: 900;
    letter-spacing: 1.3px;
    color: colors.$dark-grey-color;
    padding: 0 12px 0 16px;
    user-select: none;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    border-bottom: solid 1px colors.$grey-background-color;

    .text {
      width: 80%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .icon {
      transition: all 0.1s ease-in-out;
      transform: scale(0.7);
      margin-right: 4px;
      cursor: grab;
    }
  }
  &:not(.openable) .title:hover,
  &.selected .title:hover {
    cursor: pointer;
    background: colors.$lighter-yellow-color;
  }
  &.selected:not(.openable) {
    .title {
      position: relative;
      border-bottom-color: colors.$yellow-color;

      border-top: solid 1px colors.$yellow-color;
      margin-top: -1px;
      color: white;
      .text,
      .page {
        z-index: 1;
      }
      &:before {
        content: '';
        position: absolute;
        top: 2px;
        left: 2px;
        right: 2px;
        bottom: 2px;
        background: colors.$yellow-color;
        border-radius: 4px;
      }
    }
  }
  &.selected.openable {
    .title {
      border-bottom-color: colors.$yellow-color;
      border-top: solid 1px colors.$yellow-color;
      margin-top: -1px;
      background-color: colors.$light-yellow-color;
      color: colors.$yellow-color;
    }
    .arrow {
      display: none;
    }
  }
  .spacer {
    flex-grow: 1;
  }
  .page-number {
    margin-left: 6px;
    color: colors.$dark-grey-color;
    font-size: 10px;
    font-style: normal;
    text-transform: none;
    font-weight: normal;
    letter-spacing: normal;
    z-index: 1;
  }
  .items {
    overflow: hidden;
    border-bottom: solid 1px colors.$grey-background-color;

    &:focus {
      outline: none;
    }
    &.opened {
      border-top: solid 1px colors.$grey-background-color;
    }
    .item {
      display: flex;
      height: $item-height;
      font-size: 12px;
      padding: 2px 12px;
      user-select: none;
      align-items: center;
      gap: 7px;
      border-radius: 2px;
      cursor: pointer;

      .new-page {
        color: #1b1926;
        height: 16px;
        z-index: 10;
      }

      .icon {
        z-index: 1000;
        height: 16px;
        width: 16px;
        visibility: hidden;
        cursor: grab;

        &:active {
          cursor: grabbing;
        }
      }

      &.add {
        color: colors.$light-blue-color;
        font-weight: bold;
      }
      &:nth-child(2n) {
        background-color: colors.$lightest-grey-color;
      }

      // item is draggable
      &.draggable-mirror {
        width: 308px;
        @media only screen and (max-width: 1370px) {
          width: 247px;
        }
      }
      &:focus {
        outline: none;
      }

      .item-title {
        line-height: 26px;
        border-radius: 2px;
        width: 100%;
        height: 100%;
        font-size: 14px;

        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;

        z-index: 1;

        &.right {
          padding-left: 12px;
        }

        &.right-2 {
          padding-left: 24px;
        }

        &.right-3 {
          padding-left: 36px;
        }
      }

      &.empty {
        font-style: italic;
        color: colors.$grey-color;
      }
      &:hover {
        background-color: colors.$secondary-color;

        > .icon {
          visibility: visible;
        }
      }

      .arrow {
        display: block;
        z-index: 1;
        margin-left: -11px;
        width: 24px;
      }
      &:not(.selected) {
        .arrow {
          visibility: hidden;
        }
      }
      &.selected {
        position: relative;
        color: white;
        &:before {
          content: '';
          position: absolute;
          top: 2px;
          left: 2px;
          right: 2px;
          bottom: 2px;
          background: colors.$yellow-color;
          border-radius: 4px;
        }
        .item-title {
          font-weight: bold;
        }
        .arrow {
          display: block;
        }
      }
      &:not(.selected) {
        border-bottom: none;
      }
    }
  }

  .add-button {
    height: $item-height;
    text-transform: uppercase;
    user-select: none;
    color: colors.$light-blue-color;
    font-weight: bold;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 11px;
    margin: 2px;
    position: absolute;
    bottom: 0;
    top: 0;
    right: 0;

    .icon {
      cursor: pointer;
      display: flex;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      align-items: center;
      justify-content: center;

      color: white;
      background: colors.$light-blue-color;
      &:hover {
        background: colors.$blue-color;
      }
    }
  }
}
</style>
